import { lookupAirport, sceneryFromRecord } from "./airports";
import { PREFERRED_ICAO, seasonId, type SeasonId } from "./season";
import type { FlightType, SceneryAirport } from "./types";

/**
 * Destinations row created by Inbox → seed pack onto Destinations.
 * Filing allow-list only — not Detect inventory, not a permanent map dot.
 */
export const DEST_PACK_SEED_NOTES = "From destination pack";

/** Seed lists for Inbox generation ideas — not a live airport directory. */
export type DestPackId = "hawker_na" | "mexico" | "caribbean";

export type DestPack = {
  id: DestPackId;
  label: string;
  region: string;
  icaos: readonly string[];
  notes: string;
};

/** Hawker-reachable North American charter fields already in the catalog. Exec first. */
export const HAWKER_NA_ICAOS = [
  "CYPD",
  "CYHZ",
  "CYQY",
  "CYYT",
  "CYQM",
  "CYYG",
  "KTEB",
  "KHPN",
  "KBED",
  "CYHU",
  "CYHM",
  "CYKZ",
  "CYTZ",
  "CYKF",
  "KMMU",
  "KFRG",
  "KORH",
  "KSWF",
  "KHEF",
  "CYOW",
  "CYQB",
  "KDCA",
  "KMHT",
  "KACK",
  "KBGR",
  "KCHS",
  "KMYR",
  "KILM",
  "CYYZ",
  "CYUL",
  "KBOS",
  "KJFK",
  "KEWR",
  "KIAD",
  "KPHL",
  "KBWI",
  "KCLT",
] as const;

/** Mexico leisure / corporate pads that fit a Hawker from the east. */
export const MEXICO_ICAOS = [
  "MMUN",
  "MMPR",
  "MMSD",
  "MMTO",
  "MMMX",
  "MMCZ",
  "MMMY",
] as const;

/** Caribbean / Bermuda sun fields already used by winter mix. */
export const CARIBBEAN_ICAOS = [
  "TJSJ",
  "MYNN",
  "TXKF",
  "TBPB",
  "TNCM",
  "MDPC",
  "MKJS",
  "MWCR",
  "TNCA",
  "TIST",
] as const;

export const DEST_PACKS: DestPack[] = [
  {
    id: "hawker_na",
    label: "Hawker NA",
    region: "Northeast / Atlantic Canada",
    icaos: HAWKER_NA_ICAOS,
    notes: "Golf / corporate / VIP city-pairs. Exec / reliever first; airline hubs are the minority.",
  },
  {
    id: "mexico",
    label: "Mexico",
    region: "Mexico",
    icaos: MEXICO_ICAOS,
    notes: "Winter-sun and corporate ideas. Seed onto Destinations before filing.",
  },
  {
    id: "caribbean",
    label: "Caribbean",
    region: "Caribbean / Bermuda",
    icaos: CARIBBEAN_ICAOS,
    notes: "Southbound escape ideas. Soft-prefer when the fields are on the list.",
  },
];

export const DEST_PACK_BY_ID: Record<DestPackId, DestPack> = Object.fromEntries(
  DEST_PACKS.map((p) => [p.id, p]),
) as Record<DestPackId, DestPack>;

export function isDestPackId(value: unknown): value is DestPackId {
  return value === "hawker_na" || value === "mexico" || value === "caribbean";
}

export function destPackFor(id: DestPackId): DestPack {
  return DEST_PACK_BY_ID[id];
}

export function knownPackIcaos(ids: DestPackId[] = DEST_PACKS.map((p) => p.id)): string[] {
  const set = new Set<string>();
  for (const id of ids) {
    for (const icao of destPackFor(id).icaos) {
      if (lookupAirport(icao)) set.add(icao);
    }
  }
  return [...set];
}

const ALL_PACK_ICAO_SET = new Set(knownPackIcaos());

/**
 * Inbox / season pack seed onto Destinations — not a Detect hit, not Destinations → Add.
 * Tagged going forward; legacy catalog clones of pack ICAOs still match.
 */
export function isDestPackSeed(row: Pick<SceneryAirport, "icao" | "notes">): boolean {
  const code = row.icao.trim().toUpperCase();
  if (!code) return false;
  const notes = (row.notes ?? "").trim();
  // Destinations → Add defaults to "Manual add"; that wins even for a pack ICAO.
  if (notes.startsWith("Manual add")) return false;
  if (notes.startsWith(DEST_PACK_SEED_NOTES)) return true;
  if (!ALL_PACK_ICAO_SET.has(code)) return false;
  const catalog = lookupAirport(code);
  if (!catalog) return false;
  // Pre-0.4.39 Inbox seed used sceneryFromRecord notes verbatim.
  return notes === (catalog.notes ?? "").trim();
}

/** Catalog rows that can be added to Destinations from a pack. */
export function packScenery(id: DestPackId): SceneryAirport[] {
  return destPackFor(id)
    .icaos.map((icao) => lookupAirport(icao))
    .filter((rec): rec is NonNullable<typeof rec> => Boolean(rec))
    .map((rec) => {
      const base = sceneryFromRecord(rec);
      const extra = base.notes.trim() ? ` — ${base.notes.trim()}` : "";
      return { ...base, notes: `${DEST_PACK_SEED_NOTES}${extra}` };
    });
}

export function mergePackIntoScenery(scenery: SceneryAirport[], id: DestPackId): SceneryAirport[] {
  const have = new Set(scenery.map((s) => s.icao.toUpperCase()));
  const extras = packScenery(id).filter((s) => !have.has(s.icao.toUpperCase()));
  return extras.length ? [...scenery, ...extras] : scenery;
}

/** Season → pack mix for generation ideas (still remapped to the Destinations allow-list). */
export function packsForSeason(id: SeasonId): DestPackId[] {
  if (id === "golf") return ["hawker_na"];
  if (id === "winter") return ["caribbean", "mexico", "hawker_na"];
  if (id === "shoulder-fall") return ["caribbean", "hawker_na"];
  return ["hawker_na", "mexico"];
}

export function packsForMonth(month: number): DestPackId[] {
  return packsForSeason(seasonId(month));
}

export function preferIcaosForType(type: FlightType, packs: DestPackId[] = []): string[] {
  const set = new Set<string>(PREFERRED_ICAO[type] ?? []);
  for (const icao of knownPackIcaos(packs)) set.add(icao);
  return [...set];
}

export function packCaption(ids: DestPackId[]): string {
  if (!ids.length) return "No destination pack selected";
  return ids.map((id) => destPackFor(id).label).join(" · ");
}
